import React, { useState } from "react";
import axios from "axios";
import { FiCopy, FiDownload, FiFileText } from "react-icons/fi";
import { getSession } from "../utils/session";
import { API_URL, ORIGEN_API } from "../services/api";

// Exporta el catálogo de ejercicios de la app para que el entrenador lo use
// fuera (Excel, ChatGPT...): código, nombre, grupo muscular, material,
// instrucciones y enlace al GIF.

const MATERIAL = {
  "body weight": "Peso corporal",
  barbell: "Barra",
  "ez barbell": "Barra Z",
  "olympic barbell": "Barra olímpica",
  dumbbell: "Mancuernas",
  cable: "Polea",
  "leverage machine": "Máquina",
  "sled machine": "Máquina (prensa)",
  "smith machine": "Multipower",
  kettlebell: "Kettlebell",
  band: "Banda elástica",
  "resistance band": "Banda elástica",
  "stability ball": "Fitball",
  "medicine ball": "Balón medicinal",
  rope: "Cuerda",
  assisted: "Asistida",
  weighted: "Con lastre",
  "trap bar": "Barra hexagonal",
  roller: "Rodillo",
  bosu: "Bosu",
};

const material = (m) => (m ? MATERIAL[String(m).toLowerCase()] || m : "");
const linkGif = (g) => (g ? ORIGEN_API + g : "");
const fecha = () => new Date().toISOString().slice(0, 10);

async function cargarEjercicios() {
  const { token } = await getSession();
  const cab = { headers: { Authorization: "Bearer " + token } };
  const [catalogo, videoteca] = await Promise.all([
    axios.get(API_URL + "/personal-routines/catalogo", cab),
    axios.get(API_URL + "/videolibrary/catalogo", cab).catch(() => ({ data: [] })),
  ]);
  const instrucciones = new Map((videoteca.data || []).map((v) => [v.url, v.instrucciones || []]));
  return catalogo.data
    .map((e) => ({
      codigo: e.id,
      nombre: e.name,
      grupo: e.tipo || "Otros",
      material: material(e.equipo),
      instrucciones: instrucciones.get(e.gif) || [],
      gif: linkGif(e.gif),
    }))
    .sort((a, b) => a.grupo.localeCompare(b.grupo, "es") || a.nombre.localeCompare(b.nombre, "es"));
}

function aCSV(lista) {
  const celda = (v) => '"' + String(v ?? "").replace(/"/g, '""') + '"';
  const filas = [["Código", "Ejercicio", "Grupo muscular", "Material", "Cómo se hace", "GIF"]];
  lista.forEach((e) =>
    filas.push([e.codigo, e.nombre, e.grupo, e.material, e.instrucciones.map((p, i) => i + 1 + ". " + p).join(" "), e.gif])
  );
  return "\uFEFF" + filas.map((f) => f.map(celda).join(";")).join("\r\n");
}

function aTexto(lista, conInstrucciones) {
  const grupos = {};
  lista.forEach((e) => (grupos[e.grupo] = grupos[e.grupo] || []).push(e));
  let t =
    "CATÁLOGO DE EJERCICIOS DE TITAN TRAINING (" + lista.length + " ejercicios, " + fecha() + ")\n\n" +
    "Instrucciones para la IA: al preparar rutinas usa SOLO ejercicios de esta lista, " +
    "con su nombre exacto y su código entre corchetes, para que el entrenador pueda " +
    "cargarlos tal cual en la app.\n";
  Object.keys(grupos).forEach((g) => {
    t += "\n## " + g.toUpperCase() + " (" + grupos[g].length + ")\n";
    grupos[g].forEach((e) => {
      t += "- [" + e.codigo + "] " + e.nombre + (e.material ? " — Material: " + e.material : "") + "\n";
      if (conInstrucciones && e.instrucciones.length) {
        t += e.instrucciones.map((p, i) => "    " + (i + 1) + ". " + p).join("\n") + "\n";
      }
    });
  });
  return t;
}

function descargar(nombre, contenido, tipo) {
  const url = URL.createObjectURL(new Blob([contenido], { type: tipo }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ExportarEjercicios() {
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState("");
  const [conInstrucciones, setConInstrucciones] = useState(true);

  const hacer = async (accion) => {
    setOcupado(true);
    setAviso("");
    try {
      const lista = await cargarEjercicios();
      if (accion === "excel") {
        descargar("ejercicios-titan-" + fecha() + ".csv", aCSV(lista), "text/csv;charset=utf-8");
        setAviso("Descargados " + lista.length + " ejercicios. Ábrelo con Excel o Google Sheets.");
      } else if (accion === "texto") {
        descargar("ejercicios-titan-para-chatgpt-" + fecha() + ".txt", aTexto(lista, conInstrucciones), "text/plain;charset=utf-8");
        setAviso("Descargados " + lista.length + " ejercicios. Sube el archivo a ChatGPT.");
      } else {
        await navigator.clipboard.writeText(aTexto(lista, conInstrucciones));
        setAviso("Copiados " + lista.length + " ejercicios. Pégalos en ChatGPT.");
      }
    } catch {
      setAviso("No se pudo exportar. Vuelve a intentarlo.");
    } finally {
      setOcupado(false);
    }
  };

  return (
    <section className="app-panel" style={{ padding: 16, marginBottom: 20 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 18 }}>Exportar ejercicios</h2>
      <p style={{ margin: "0 0 12px", opacity: 0.75, fontSize: 14 }}>
        Todo el catálogo de la app con su código, grupo muscular, material, cómo se hace y el GIF. Para Excel o
        para preparar rutinas con ChatGPT.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <button type="button" className="app-btn app-btn-sm" disabled={ocupado} onClick={() => hacer("excel")}>
          <FiDownload aria-hidden="true" /> Excel (.csv)
        </button>
        <button type="button" className="app-btn app-btn-sm" disabled={ocupado} onClick={() => hacer("texto")}>
          <FiFileText aria-hidden="true" /> Para ChatGPT (.txt)
        </button>
        <button type="button" className="app-btn app-btn-quiet app-btn-sm" disabled={ocupado} onClick={() => hacer("copiar")}>
          <FiCopy aria-hidden="true" /> Copiar para ChatGPT
        </button>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 14 }}>
        <input type="checkbox" checked={conInstrucciones} onChange={(e) => setConInstrucciones(e.target.checked)} />
        Incluir cómo se hace cada ejercicio en el texto para ChatGPT
      </label>
      {aviso && <p style={{ margin: "10px 0 0", fontSize: 14, fontWeight: 600 }}>{ocupado ? "Preparando…" : aviso}</p>}
    </section>
  );
}
